import { FC, useMemo, useState } from 'react'
import { Badge, Popover, Space, Spin, Typography } from 'antd'
import { skipToken } from '@reduxjs/toolkit/query'
import CalculationPosition from '../../../../../../types/api/calculation-position'
import calculateStockAmount from '../../../../../../helpers/entities/stock/calculate-stock-amount'
import MaterialWidthOptionLabel from '../../../../../../components/materials/material-width-option-label'
import useGroupedMaterialsByType from '../../../../../../helpers/render/use-grouped-materials-by-type'
import { useGetMaterialsQuery } from '../../../../../../store/api/materials-api'

const { Text } = Typography

interface AdditionalMaterialsNamesProps {
    data: CalculationPosition
}

const AdditionalMaterialsNames: FC<AdditionalMaterialsNamesProps> = ({ data }) => {
    const materials = data.additional_materials || []
    const [open, setOpen] = useState(false)

    const materialDocIds = useMemo(
        () =>
            materials
                .map(material => material.documentId)
                .filter((id): id is string => Boolean(id)),
        [materials]
    )

    const queryArg = useMemo(() => {
        if (!open || materialDocIds.length === 0) {
            return skipToken
        }

        return {
            filters: {
                documentId: {
                    $in: materialDocIds,
                },
            },
            populate: {
                material_type: {
                    fields: ['documentId', 'name'],
                },
                stocks: {
                    populate: {
                        stock_reserves: true,
                    },
                },
            },
        }
    }, [open, materialDocIds])

    const { data: materialsResponse, isFetching } = useGetMaterialsQuery(queryArg)

    const materialsForPopover = materialsResponse?.data?.length
        ? materialsResponse.data
        : materials
    const groupedMaterials = useGroupedMaterialsByType(materialsForPopover as any)

    if (materials.length === 0) {
        return null
    }

    const summary = materials.length === 1 ? `${materials[0].width} мм` : `${materials.length} шт.`

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            content={
                isFetching && !materialsResponse?.data?.length ? (
                    <Spin size='small' />
                ) : (
                    <Space direction='vertical' style={{ minWidth: 200 }}>
                        {groupedMaterials.map(group => (
                            <div key={group.type.documentId}>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                    {group.type.name}
                                </Text>
                                <Space direction='vertical' style={{ marginLeft: 0 }}>
                                    {group.materials.map(material => {
                                        const stock =
                                            material.stocks?.reduce(
                                                (sum, stockItem) =>
                                                    sum + calculateStockAmount(stockItem),
                                                0
                                            ) || 0

                                        return (
                                            <MaterialWidthOptionLabel
                                                key={material.documentId}
                                                width={material.width}
                                                stock={stock}
                                            />
                                        )
                                    })}
                                </Space>
                            </div>
                        ))}
                    </Space>
                )
            }
            placement='leftBottom'
        >
            <span>
                {materials.length > 1 && <Badge count={materials.length} />} {summary}
            </span>
        </Popover>
    )
}

export default AdditionalMaterialsNames
